/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Images", description = "Manage the image library that Lifecycle Studio draws from when generating messages — upload brand imagery, edit images, tag them for better agent selection, and connect external asset sources.", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Images",
        description: "Manage the image library that Lifecycle Studio draws from when generating messages — upload brand imagery, edit images, tag them for better agent selection, and connect external asset sources.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Upload images",
          "depth": 0,
          "id": "upload-images"
        }, {
          "value": "Search images",
          "depth": 0,
          "id": "search-images"
        }, {
          "value": "Edit an image",
          "depth": 0,
          "id": "edit-an-image"
        }, {
          "value": "Manage versions",
          "depth": 0,
          "id": "manage-versions"
        }, {
          "value": "Tag images",
          "depth": 0,
          "id": "tag-images"
        }, {
          "value": "Create and edit tags",
          "depth": 1,
          "id": "create-and-edit-tags"
        }, {
          "value": "Image guardrails",
          "depth": 0,
          "id": "image-guardrails"
        }, {
          "value": "Connect an external source",
          "depth": 0,
          "id": "connect-an-external-source"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      ol: "ol",
      li: "li",
      h3: "h3",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsx("ul", {
                children: _jsx("li", {
                  children: _jsx(_components.a, {
                    href: "/lifecycle-studio/overview#get-started",
                    children: "Lifecycle Studio setup complete"
                  })
                })
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "The image library stores the visual assets Lifecycle Studio can use in generated messages. Upload brand imagery, tag images to help the agent choose relevant assets, and connect external sources for automatic import."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/images/ls-images-overview.png",
          alt: "Images tab showing uploaded images with tags and metadata"
        })
      }), "\n", _jsx(_components.p, {
        children: "The image library gives the agent access to your brand's visual elements: product photography, lifestyle images, icons, backgrounds, and other creative files. When the agent generates a message, it selects appropriate images based on the campaign context, message content, and image tags."
      }), "\n", _jsx(_components.p, {
        children: "Upload images when you have new brand assets, tag them so the agent can find the right ones, and connect external sources so new assets flow in automatically."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "upload-images",
        children: "Upload images"
      }), "\n", _jsx(_components.p, {
        children: "To upload images:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "https://app.hightouch.com/lifecycle-studio/images?from=docs",
              children: "Lifecycle Studio → Images"
            })
          }), " and select ", _jsx(_components.strong, {
            children: "Upload"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select files or drag and drop images into the upload area."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Each image shows a thumbnail, filename, file size, and assigned tags. Select an image to see its description and tags, open the editor, or view its history."
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Upload a variety of image types: product shots, lifestyle images, icons, and backgrounds. A larger, more varied library gives the agent more options and produces more varied output."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "search-images",
        children: "Search images"
      }), "\n", _jsxs(_components.p, {
        children: ["Search by filename, or switch to ", _jsx(_components.strong, {
          children: "AI-powered search"
        }), " to rank results by how relevant they are to your query rather than by filename. This helps when you remember what an image shows but not what it's called."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/images/ls-images-ai-search.png",
          alt: "AI-powered image search"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "edit-an-image",
        children: "Edit an image"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/images/ls-images-editor.png",
          alt: "Image editor with layers, text controls, and positioning"
        })
      }), "\n", _jsx(_components.p, {
        children: "The image editor is a layer-based design tool built into Lifecycle Studio. Select an image from the library and open the editor to modify text, reposition elements, adjust fonts, colors, and sizing, and control layer ordering. An agent chat is available at the bottom of the editor for prompt-based edits."
      }), "\n", _jsx(_components.p, {
        children: "Edits are saved as new versions. The original image is preserved in the image history."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "manage-versions",
        children: "Manage versions"
      }), "\n", _jsx(_components.p, {
        children: "Each image maintains a visual history of edits. Select an image and open the history view to browse a timeline of versions from the original to the current state. Select any previous version to preview it or revert to it."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/images/ls-images-image-history.png",
          alt: "Image history showing version timeline"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "tag-images",
        children: "Tag images"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The Tagging and Integrations tabs require the ", _jsx(_components.strong, {
            children: "Configure Agents"
          }), " permission. If you don't see these tabs, contact your workspace admin. See ", _jsx(_components.a, {
            href: "/workspace-management/roles#agents",
            children: "Roles"
          }), " for details."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/images/ls-images-tagging-overview.png",
          alt: "Tagging tab showing tag categories with values"
        })
      }), "\n", _jsx(_components.p, {
        children: "Tags help the agent select appropriate images for each message. For example, tags like \"studio shot,\" \"outdoor,\" \"product,\" \"lifestyle,\" or \"seasonal\" help it match images to the creative context. You can tag images individually or in bulk. Tags are freeform, so create whatever labels make sense for your brand's library."
      }), "\n", _jsx(_components.h3, {
        id: "create-and-edit-tags",
        children: "Create and edit tags"
      }), "\n", _jsxs(_components.p, {
        children: ["To create a tag, go to the ", _jsx(_components.strong, {
          children: "Tagging"
        }), " tab and select ", _jsx(_components.strong, {
          children: "Add tag category"
        }), ". Enter a tag name, identification guidance (to help the agent understand when to apply this tag), and allowed values. Select an existing tag to edit its name, guidance, or values."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "image-guardrails",
        children: "Image guardrails"
      }), "\n", _jsxs(_components.p, {
        children: ["The agent follows guardrails configured in ", _jsx(_components.a, {
          href: "/context-hub/guardrails#set-email-guardrails",
          children: "Context Hub"
        }), " when selecting and placing images in generated messages. Configure image guardrails alongside your brand guidelines to control the agent's image behavior."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "connect-an-external-source",
        children: "Connect an external source"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/images/ls-images-integrations-overview.png",
          alt: "Integrations tab showing connected asset sources"
        })
      }), "\n", _jsx(_components.p, {
        children: "Connect Lifecycle Studio to external asset sources for automatic import. When connected, new images added to your external source become available in the image library without manual re-uploading."
      }), "\n", _jsx(_components.p, {
        children: "To connect an external source:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "https://app.hightouch.com/lifecycle-studio/images?from=docs",
              children: "Lifecycle Studio → Images → Integrations"
            })
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the platform you want to connect."
        }), "\n", _jsx(_components.li, {
          children: "Authenticate and select the folders or libraries to sync."
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/lifecycle-studio/messages",
            children: "Messages"
          }), " — Create and edit lifecycle messages"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/lifecycle-studio/templates",
            children: "Templates"
          }), " — Manage structural starting points for messages"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/lifecycle-studio/campaigns",
            children: "Campaigns"
          }), " — Plan and send end-to-end lifecycle campaigns"]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
